import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_RgbColorPickerFromImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image',
			name: 'RGB Color Picker from Image — Get Exact RGB Values by Lift',
			description:
				'Upload any photo and get the exact RGB value of any color with a live eyedropper — plus HEX and HSL. Pick RGB from any image instantly. Try Lift today!',
			url: 'https://liftapp.ai/rgb-color-picker-from-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/rgb-color-picker-from-image#product' },
				{ '@id': 'https://liftapp.ai/rgb-color-picker-from-image#howto' },
				{ '@id': 'https://liftapp.ai/rgb-color-picker-from-image#benefits' },
				{ '@id': 'https://liftapp.ai/rgb-color-picker-from-image#faq' },
				{ '@id': 'https://liftapp.ai/rgb-color-picker-from-image#why-lift' },
				{
					'@id': 'https://liftapp.ai/rgb-color-picker-from-image#explore-tools',
				},
				{ '@id': 'https://liftapp.ai/rgb-color-picker-from-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image#product',
			name: 'RGB Color Picker from Image',
			description:
				'Upload a photo, hover the eyedropper over any pixel, and get the exact RGB value instantly — along with HEX and HSL codes ready to copy. Try Lift today.',
			url: 'https://liftapp.ai/rgb-color-picker-from-image',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: 'Add any image to the editor and the live eyedropper activates instantly on the canvas.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Hover to preview the RGB value',
					text: 'The eyedropper tracks every pixel and displays the RGB value in real time as you move across the image.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Click to lock your color',
					text: 'Tap any spot to fix the selection; the exact RGB, HEX, and HSL values appear instantly in the side panel.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Copy and use anywhere',
					text: 'Copy the RGB code with one click and use it directly in CSS, Figma, Krita, or any creative tool.',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image#benefits',
			name: 'Who can benefit from the Lift RGB Color Picker?',
			description:
				'Lift RGB Color Picker helps web developers, game designers, illustrators, UI/UX designers, and content creators pick precise RGB values from any image with HEX and HSL alongside.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: "Web & front-end developers — Pick precise RGB values from any screenshot or mockup to use directly in CSS rgb() declarations. Lift's eyedropper reads any pixel and returns the exact RGB tuple without switching tools.",
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Game designers — Match colors from reference art or concept imagery with pixel-accurate RGB values for Unity, Godot, or any game engine. The live eyedropper identifies any shade from any photo in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Digital illustrators — Source exact RGB values from reference photos to replicate in Procreate, Krita, or Affinity without manual guesswork. The eyedropper locks onto any pixel and returns the precise code instantly.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'UI/UX designers — Pull accurate RGB codes from brand photography to apply directly in Figma or Sketch color palettes. Lift surfaces the exact value of any color in your image with one click.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Content creators & social media managers — Maintain visual consistency across posts by identifying exact RGB values from signature imagery. Extract colors from any photo in seconds to replicate across every piece of content.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift RGB color picker from image — matcha latte café flatlay with HEX, RGB, and HSL color values',
				description:
					'RGB color picker on a matcha café flatlay showing HEX code #7BAE7F and all three color format values',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift RGB Color Picker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "The Lift RGB Color Picker is an image-based tool that identifies the exact RGB value of any color in your photo using a live eyedropper. It also returns the same color in HEX and HSL, and generates a palette of the image's dominant colors.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift RGB Color Picker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a photo and hover the eyedropper over any area to preview the RGB value in real time. Click to lock your selection and instantly copy the RGB, HEX, or HSL code from the side panel.',
					},
				},
				{
					'@type': 'Question',
					name: 'Does the Lift RGB Color Picker also show HEX and HSL values?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — every color you pick is displayed in RGB, HEX, and HSL simultaneously in the panel. Each format has its own copy button, so you grab exactly the value your workflow needs.',
					},
				},
				{
					'@type': 'Question',
					name: 'What image formats does the Lift RGB Color Picker support?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'You can upload JPG, PNG, WEBP, and HEIC files in any size or resolution. The tool works equally well with product shots, screenshots, landscapes, and scanned artwork.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image#why-lift',
			name: 'Why Lift RGB Color Picker',
			description:
				'High-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift RGB color picker from image — watercolor art supplies photo with blue HEX and RGB code',
				description:
					'RGB color picker from image on a watercolor painting supplies flatlay showing HEX code #3A7BD5 and palette',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/rgb-color-picker-from-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'RGB Color Picker from Image',
					item: 'https://liftapp.ai/rgb-color-picker-from-image',
				},
			],
		},
	],
};
